import { useAuthenticator } from '@aws-amplify/ui-react';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../redux/store';
import { RoleName } from '../types/user';

export const useAuthenticatedUser = () => {
	const { user: authUser, authStatus } = useAuthenticator();
	const { currentUser: user, readUserRequest } = useAppSelector((state) => state.user);

	const groups: string[] = useMemo(
		() => authUser?.getSignInUserSession()?.getAccessToken().payload['cognito:groups'] ?? [],
		[authUser]
	);

	const roles = useMemo(() => {
		const r = new Set<RoleName>();
		if (user) {
			user.roles.forEach((role) => r.add(role.roleName));
		}
		return Array.from(r.values());
	}, [user]);

	const getOrganisationRoles = useCallback(
		(organisationId: string) => {
			if (!user) return [];
			return user.roles.filter((role) => role.organisationId === organisationId).map((role) => role.roleName);
		},
		[user]
	);

	const hasOrganisationRole = useCallback(
		(organisation: string, role: RoleName) => {
			return getOrganisationRoles(organisation).includes(role);
		},
		[user]
	);

	const userId = useMemo(() => user?.id, [user]);

	const authenticated = useMemo(() => authStatus === 'authenticated', [authStatus]);
	const unauthenticated = useMemo(() => authStatus === 'unauthenticated', [authStatus]);
	const loading = useMemo(
		() => readUserRequest.status === 'pending' || readUserRequest.status === 'idle',
		[authStatus, readUserRequest.status]
	);

	return {
		authenticated,
		unauthenticated,
		groups,
		roles,
		getOrganisationRoles,
		hasOrganisationRole,
		user,
		loading,
		readUserRequest,
		userId,
		authUser
	};
};
